import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { PageMeta } from '@/components/seo/PageMeta'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  ApprovalStatusBadge,
  ApprovalTimeline,
  ApprovalCommentsList,
  ApprovalActionsPanel,
} from '@/components/approval'
import { approvalWorkflowService } from '@/lib/api/services'
import type { ApprovalRequestDetail, ApprovalAction } from '@/lib/api/types'
import { useToast } from '@/components/ui/use-toast'
import { useConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  FileText,
  Clock,
  User,
  GitBranch,
  Layers,
  MessageSquare,
  Eye,
  XCircle,
} from 'lucide-react'
import { getAvatarColor } from '@/lib/avatar-colors'

export default function ApprovalDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { confirm, ConfirmDialogComponent } = useConfirmDialog()
  const [approval, setApproval] = useState<ApprovalRequestDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [processing, setProcessing] = useState(false)
  const [cancelling, setCancelling] = useState(false)

  const loadApproval = useCallback(async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)
      const data = await approvalWorkflowService.getRequest(id)
      setApproval(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load approval request')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadApproval()
  }, [loadApproval])

  const handleProcess = async (action: ApprovalAction, comments?: string) => {
    if (!approval) return

    setProcessing(true)
    try {
      await approvalWorkflowService.processApproval(approval.id, { action, comments })
      toast({
        title: 'Success',
        description:
          action === 'Approve'
            ? 'Template approved successfully'
            : action === 'Reject'
            ? 'Template rejected'
            : 'Changes requested',
      })
      await loadApproval()
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to process approval',
        variant: 'destructive',
      })
    } finally {
      setProcessing(false)
    }
  }

  const handleAddComment = async (content: string) => {
    if (!approval) return

    try {
      await approvalWorkflowService.addComment(approval.id, { content })
      await loadApproval()
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to add comment',
        variant: 'destructive',
      })
    }
  }

  const handleResolveComment = async (commentId: string) => {
    if (!approval) return

    try {
      await approvalWorkflowService.resolveComment(approval.id, commentId)
      await loadApproval()
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to resolve comment',
        variant: 'destructive',
      })
    }
  }

  const handleCancel = async () => {
    if (!approval) return

    const confirmed = await confirm({
      title: 'Cancel Approval Request',
      description: 'Are you sure you want to cancel this approval request? This action cannot be undone.',
      confirmLabel: 'Cancel Request',
      variant: 'destructive',
    })
    if (!confirmed) return

    setCancelling(true)
    try {
      await approvalWorkflowService.cancelRequest(approval.id)
      toast({
        title: 'Success',
        description: 'Approval request cancelled',
      })
      navigate('/approval/pending')
    } catch (err) {
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to cancel request',
        variant: 'destructive',
      })
    } finally {
      setCancelling(false)
    }
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
        <p className="text-neutral-500 dark:text-neutral-400">Loading approval request...</p>
      </div>
    )
  }

  if (error || !approval) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <div className="p-4 rounded-full bg-red-100 dark:bg-red-900/30">
          <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
        </div>
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          {error || 'Approval request not found'}
        </h2>
        <Button variant="outline" onClick={() => navigate('/approval/pending')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Approvals
        </Button>
      </div>
    )
  }

  const canAct =
    (approval.status === 'PendingReview' || approval.status === 'InReview') &&
    (approval.canApprove || approval.canReject || approval.canRequestChanges)

  const isTerminal = approval.status === 'Approved' || approval.status === 'Rejected'

  return (
    <>
      {ConfirmDialogComponent}
      <PageMeta
        title={`Approval: ${approval.templateName}`}
        description="Review and approve template"
      />

      <div className="space-y-6">
        {/* Back Link */}
        <Link
          to="/approval/pending"
          className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-white transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Approvals
        </Link>

        {/* Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 shadow-lg shadow-blue-500/25">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                  {approval.templateName}
                </h1>
                <ApprovalStatusBadge status={approval.status} />
              </div>
              <p className="text-neutral-500 dark:text-neutral-400 mt-1">
                {approval.workflowName}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Link to={`/templates/${approval.templateId}`}>
              <Button variant="outline">
                <Eye className="h-4 w-4 mr-2" />
                View Template
              </Button>
            </Link>
            {!isTerminal && (
              <Button
                variant="outline"
                onClick={handleCancel}
                disabled={cancelling}
                className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
              >
                {cancelling ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <XCircle className="h-4 w-4 mr-2" />
                )}
                Cancel Request
              </Button>
            )}
          </div>
        </div>

        {/* Main Content */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Action Panel */}
            {canAct && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Take Action</CardTitle>
                </CardHeader>
                <CardContent>
                  <ApprovalActionsPanel
                    approval={approval}
                    onProcess={handleProcess}
                    processing={processing}
                  />
                </CardContent>
              </Card>
            )}

            {/* Tabs */}
            <Tabs defaultValue="timeline">
              <TabsList>
                <TabsTrigger value="timeline">
                  <Layers className="h-4 w-4 mr-2" />
                  Timeline
                </TabsTrigger>
                <TabsTrigger value="comments">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Comments
                  {approval.comments.filter((c) => !c.isResolved).length > 0 && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      {approval.comments.filter((c) => !c.isResolved).length}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="timeline" className="mt-4">
                <Card>
                  <CardContent className="p-4">
                    <ApprovalTimeline
                      workflow={approval.workflow}
                      stageResults={approval.stageResults}
                      currentStageId={approval.currentStageId}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="comments" className="mt-4">
                <Card>
                  <CardContent className="p-4">
                    <ApprovalCommentsList
                      comments={approval.comments}
                      onAddComment={handleAddComment}
                      onResolveComment={handleResolveComment}
                    />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Info Card */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Request Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Current Stage */}
                <div className="p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
                  <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 mb-1">
                    <Layers className="h-3 w-3" />
                    Current Stage
                  </div>
                  <p className="font-medium text-neutral-900 dark:text-neutral-100">
                    {approval.currentStageName}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">
                    Stage {approval.currentStageOrder} of {approval.totalStages}
                  </p>
                </div>

                {/* Workflow */}
                <div className="p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
                  <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 mb-1">
                    <GitBranch className="h-3 w-3" />
                    Workflow
                  </div>
                  <p className="font-medium text-neutral-900 dark:text-neutral-100">
                    {approval.workflowName}
                  </p>
                </div>

                {/* Submitted By */}
                <div className="p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
                  <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 mb-2">
                    <User className="h-3 w-3" />
                    Submitted By
                  </div>
                  <div className="flex items-center gap-2">
                    <Avatar className="h-8 w-8">
                      <AvatarFallback className={`text-xs ${getAvatarColor(approval.submittedBy || approval.submittedByName).bg} ${getAvatarColor(approval.submittedBy || approval.submittedByName).text}`}>
                        {getInitials(approval.submittedByName)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {approval.submittedByName}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Submitted At */}
                <div className="p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
                  <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 mb-1">
                    <Clock className="h-3 w-3" />
                    Submitted At
                  </div>
                  <p className="text-sm text-neutral-900 dark:text-neutral-100">
                    {formatDate(approval.submittedAt)}
                  </p>
                </div>

                {/* Completed At (if terminal) */}
                {approval.completedAt && (
                  <div className="p-3 rounded-lg bg-neutral-50 dark:bg-neutral-800/50">
                    <div className="flex items-center gap-2 text-xs text-neutral-500 dark:text-neutral-400 mb-1">
                      <Clock className="h-3 w-3" />
                      {approval.status === 'Approved' ? 'Approved At' : 'Rejected At'}
                    </div>
                    <p className="text-sm text-neutral-900 dark:text-neutral-100">
                      {formatDate(approval.completedAt)}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to={`/templates/${approval.templateId}`} className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <Eye className="h-4 w-4 mr-2" />
                    View Template
                  </Button>
                </Link>
                <Link to={`/templates/${approval.templateId}/edit`} className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <FileText className="h-4 w-4 mr-2" />
                    Edit Template
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}
